import {
  Box,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconBuildingBank } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { AccountApi } from "../../apis";
import type { BankAccountPurpose, TenantBankAccount } from "../../apis/account";
import { Message } from "../../utils/message";
import { PURPOSE_LABELS } from "../../utils/bank-account-labels";

const PURPOSE_OPTIONS = [
  { value: "RECEIVE", label: PURPOSE_LABELS.RECEIVE },
  { value: "PAY", label: PURPOSE_LABELS.PAY },
  { value: "BOTH", label: PURPOSE_LABELS.BOTH },
];

const header = (
  <Group gap="sm" wrap="nowrap">
    <ThemeIcon size={38} radius="md" variant="light" color="brand">
      <IconBuildingBank size={20} />
    </ThemeIcon>
    <Box>
      <Title order={4}>Дансны мэдээлэл</Title>
      <Text size="sm" c="dimmed">Зориулалт ба төлвийг шинэчилнэ</Text>
    </Box>
  </Group>
);

interface Props {
  account: TenantBankAccount | null;
  opened: boolean;
  onClose: () => void;
  onSaved: () => void;
}

// Edits only the mutable fields (spec §3.1 "Засах") — bank/number/name are fixed by
// the verification and never editable.
export function EditBankAccountDrawer({ account, opened, onClose, onSaved }: Props) {
  const [shortName, setShortName] = useState("");
  const [purpose, setPurpose] = useState<BankAccountPurpose>("RECEIVE");
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [shortNameError, setShortNameError] = useState<string | undefined>();

  useEffect(() => {
    if (account) {
      setShortName(account.shortName ?? "");
      setPurpose(account.purpose);
      setIsDefault(account.isDefault);
      setIsActive(account.isActive);
      setShortNameError(undefined);
    }
  }, [account]);

  if (!account) return <Modal opened={opened} onClose={onClose} centered size={520} title={header} />;

  const onSave = async () => {
    if (!shortName.trim()) { setShortNameError("Дансны товч нэр шаардлагатай."); return; }
    setLoading(true);
    try {
      await AccountApi.update(account.id, {
        shortName: shortName.trim(),
        purpose,
        isDefault,
        isActive
      });
      Message.success("Данс шинэчлэгдлээ.");
      onSaved();
      onClose();
    } catch {
      setLoading(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} centered size={520} title={header}>
      <Stack gap="md">
        <Box>
          <Text size="xs" c="dimmed">{account.bankName ?? account.bankCode} · {account.accountNumber}</Text>
          <Text fw={600}>{account.accountName}</Text>
        </Box>
        <TextInput
          label="Дансны товч нэр"
          required
          value={shortName}
          onChange={(ev) => setShortName(ev.currentTarget.value)}
          error={shortNameError}
        />
        <Box>
          <Text size="sm" fw={500} mb={6}>Зориулалт</Text>
          <SegmentedControl
            fullWidth
            value={purpose}
            onChange={(v) => setPurpose(v as BankAccountPurpose)}
            data={PURPOSE_OPTIONS}
          />
        </Box>
        <Switch label="Үндсэн данс" checked={isDefault} onChange={(ev) => setIsDefault(ev.currentTarget.checked)} />
        <Switch label="Идэвхтэй" checked={isActive} onChange={(ev) => setIsActive(ev.currentTarget.checked)} />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={loading}>Болих</Button>
          <Button color="green" onClick={onSave} loading={loading}>Хадгалах</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
